import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, TriangleAlert, Users, CalendarCheck2, CircleDollarSign } from "lucide-react";
import { track } from "@/lib/analytics";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const FIELDS = [
  {
    key: "commission",
    label: "Average gross commission income per closed transaction",
    min: 2000,
    max: 30000,
    step: 250,
    initial: 9000,
    format: (v) => usd.format(v),
  },
  {
    key: "leads",
    label: "Number of new leads per month",
    min: 1,
    max: 150,
    step: 1,
    initial: 30,
    format: (v) => `${v}`,
  },
  {
    key: "inconsistent",
    label: "Estimated percentage of leads receiving inconsistent follow-up",
    min: 0,
    max: 100,
    step: 5,
    initial: 40,
    format: (v) => `${v}%`,
  },
  {
    key: "closeRate",
    label: "Approximate lead-to-client close rate",
    min: 1,
    max: 20,
    step: 0.5,
    initial: 5,
    format: (v) => `${v}%`,
  },
];

const labelCls = "block text-sm font-semibold text-slate-200 mb-2";
const numberCls =
  "w-28 shrink-0 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-semibold text-right focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-[box-shadow,border-color] duration-200";

export default function RevenueCalculator() {
  const [values, setValues] = useState(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, f.initial]))
  );
  const startedRef = useRef(false);
  const touchedRef = useRef(new Set());

  const update = (key, raw) => {
    const field = FIELDS.find((f) => f.key === key);
    let v = Number(raw);
    if (Number.isNaN(v)) v = field.min;
    v = Math.min(field.max, Math.max(field.min, v));
    setValues((prev) => ({ ...prev, [key]: v }));
    if (!startedRef.current) {
      startedRef.current = true;
      track("calculator_started");
    }
    touchedRef.current.add(key);
    if (touchedRef.current.size >= 2 && !touchedRef.current.has("__completed")) {
      touchedRef.current.add("__completed");
      track("calculator_completed");
    }
  };

  const leadsAtRisk = (values.leads * values.inconsistent) / 100;
  const potentialClosed = (leadsAtRisk * values.closeRate) / 100;
  const monthlyOpportunity = potentialClosed * values.commission;
  const annualOpportunity = monthlyOpportunity * 12;

  return (
    <section id="calculator" data-testid="calculator-section" className="py-20 md:py-28 bg-[#0D1220]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            What Could Inconsistent Follow-Up <span className="text-gradient">Be Worth?</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl text-slate-200 leading-relaxed">
            Use the sliders to estimate the economic value that may be moving through your pipeline
            without consistent response and follow-up.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 space-y-7"
          >
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label htmlFor={`calc-number-${f.key}`} className={labelCls}>
                  {f.label}
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    data-testid={`calc-slider-${f.key}`}
                    aria-label={f.label}
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={values[f.key]}
                    onChange={(e) => update(f.key, e.target.value)}
                    className="flex-1 accent-cyan-400 h-2 cursor-pointer"
                  />
                  <input
                    id={`calc-number-${f.key}`}
                    type="number"
                    data-testid={`calc-number-${f.key}`}
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={values[f.key]}
                    onChange={(e) => update(f.key, e.target.value)}
                    className={numberCls}
                  />
                </div>
                <p className="mt-1.5 text-xs text-slate-500">{f.format(values[f.key])}</p>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl border border-indigo-400/30 bg-indigo-500/[0.06] p-7"
            data-testid="calc-results-card"
          >
            <h3 className="font-heading font-bold text-xl text-white">
              Potential Commission Opportunity at Risk
            </h3>

            <p
              data-testid="calc-annual-value"
              className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-gradient"
            >
              {usd.format(annualOpportunity)}
            </p>
            <p className="mt-3 text-base md:text-lg text-slate-200 leading-relaxed">
              Based on the assumptions you entered, approximately{" "}
              <span className="font-semibold text-white">{usd.format(annualOpportunity)}</span> in
              annual commission opportunity may be moving through your pipeline without consistent
              follow-up.
            </p>

            <div className="mt-7 grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                <p data-testid="calc-leads-at-risk" className="font-heading font-bold text-lg text-cyan-300 flex items-center gap-1.5">
                  <Users size={16} strokeWidth={2} className="shrink-0" />
                  {leadsAtRisk.toFixed(1)}
                </p>
                <p className="mt-1 text-xs text-slate-400 leading-snug">Leads at risk per month</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                <p data-testid="calc-transactions" className="font-heading font-bold text-lg text-cyan-300 flex items-center gap-1.5">
                  <CalendarCheck2 size={16} strokeWidth={2} className="shrink-0" />
                  {potentialClosed.toFixed(1)}
                </p>
                <p className="mt-1 text-xs text-slate-400 leading-snug">
                  Potential closed transactions influenced
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                <p data-testid="calc-monthly-value" className="font-heading font-bold text-lg text-cyan-300 flex items-center gap-1.5">
                  <CircleDollarSign size={16} strokeWidth={2} className="shrink-0" />
                  {usd.format(monthlyOpportunity)}
                </p>
                <p className="mt-1 text-xs text-slate-400 leading-snug">Estimated monthly opportunity</p>
              </div>
            </div>

            <div
              data-testid="calc-disclaimer"
              className="mt-7 flex items-start gap-2.5 rounded-xl border border-amber-400/20 bg-amber-500/[0.06] p-4"
            >
              <TriangleAlert size={16} className="text-amber-300 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200/90 leading-relaxed">
                This estimate is illustrative only and is based entirely on the assumptions entered.
                It does not represent guaranteed revenue, actual losses, or expected results.
              </p>
            </div>

            <Link
              to="/lead-flow-audit"
              data-testid="calc-audit-cta"
              data-track="calculator_audit_clicked"
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)]"
            >
              See Where Your Lead Flow May Be Breaking
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
